import React, { useState } from 'react';
import { adminAPI } from '../../services/api';
import { Archive, Loader2 } from 'lucide-react';

// Effects whose entry in external_errors means "the call itself failed" — a
// Stripe outage, a network blip. Those are genuinely retryable: pressing the
// button again re-runs the very thing that broke.
//
// Every other effect means the opposite. The service looked, decided there was
// nothing for it to cancel, and said so on purpose: a comped subscription with
// no Stripe id, or a live subscription at Stripe that our database has no record
// of and therefore deliberately did not touch. Re-running archive will reach the
// same conclusion forever, so offering "Retry Stripe cancel" for those would be
// a button that cannot work. The admin has to go into Stripe.
const RETRYABLE_EFFECTS = ['stripe_subscription_cancel', 'stripe_subscription_lookup'];

// Reversible counterpart to DeleteAccountModal. Deliberately has NO typed-word
// gate: archiving is recoverable, and putting the same friction on both actions
// teaches admins to type through the one that isn't.
const ArchiveAccountModal = ({ account, onClose, onArchived }) => {
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [externalErrors, setExternalErrors] = useState([]);

    const handleArchive = async () => {
        setSubmitting(true);
        setError('');
        setExternalErrors([]);
        try {
            const res = await adminAPI.archiveBillingAccount(account.id, { reason });

            // A 2xx here does NOT mean the archive fully succeeded. The service
            // cancels the Stripe subscription outside its transaction and rescues
            // the failure into external_errors, so the account can come back frozen
            // on our side while the customer's subscription is still live at Stripe
            // and still charging them. Discarding the response and calling
            // onArchived() on any 2xx is what made that invisible to the admin.
            const failures = res?.external_errors || [];
            if (failures.length) {
                setExternalErrors(failures);
                setSubmitting(false);
                return;
            }

            onArchived();
        } catch (err) {
            const violations = err?.response?.data?.violations;
            setError(violations?.length
                ? violations.map((v) => v.message).join(' ')
                : err?.response?.data?.message || 'Could not archive this account.');
            setSubmitting(false);
        }
    };

    // "Something is outstanding" and "a call failed" are different states and the
    // modal must not blur them. A left-active subscription is not a failure of
    // the archive — the archive completed — it is a billing issue the admin now
    // owns, and telling them to retry would send them round a loop that changes
    // nothing while the customer keeps paying.
    const retryable = externalErrors.filter((e) => RETRYABLE_EFFECTS.includes(e.effect));
    const outstanding = externalErrors.filter((e) => !RETRYABLE_EFFECTS.includes(e.effect));
    const partiallyFailed = externalErrors.length > 0;
    const canRetry = retryable.length > 0;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-lg rounded-lg border border-border bg-card p-5 shadow-lg">
                <div className="mb-4 flex items-start gap-3">
                    <Archive className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                    <div>
                        <h2 className="text-lg font-semibold text-foreground">Archive account</h2>
                        <p className="text-sm text-muted-foreground">
                            Freezes {account.owner?.email}. Their workspaces become unreachable,
                            scheduled runs and alerts stop, and any paid subscription is cancelled.
                            Nothing is deleted &mdash; this can be restored later.
                        </p>
                    </div>
                </div>

                {error && (
                    <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                        {error}
                    </div>
                )}

                {/* The freeze landed; a vendor call did not. Naming the failed
                    effects is the whole point — a silent close here leaves the
                    customer paying for an account they can no longer reach. */}
                {canRetry && (
                    <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                        <div className="mb-1 font-semibold">
                            Account archived, but {retryable.length} external call
                            {retryable.length === 1 ? '' : 's'} failed
                        </div>
                        <p className="mb-2 text-destructive/90">
                            The workspaces are frozen, but the following ran afterwards and did
                            not succeed. Until the subscription cancel goes through, the customer
                            is still being charged. Retry, or cancel it in Stripe manually.
                        </p>
                        <ul className="list-disc space-y-1 pl-5">
                            {retryable.map((e, i) => (
                                <li key={`${e.effect}-${i}`}>
                                    <span className="font-mono text-xs">{e.effect}</span>
                                    {e.error ? ` — ${e.error}` : ''}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Nothing failed here: the archive completed and we deliberately
                    did not cancel. Framing this as an error would teach admins to
                    click Retry at a button that cannot help, and would eventually
                    teach them to ignore the red box that sometimes can. It is a
                    completed archive with an outstanding billing issue — the fix
                    is in Stripe, not in this modal. */}
                {outstanding.length > 0 && (
                    <div className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-300">
                        <div className="mb-1 font-semibold">
                            Account archived &mdash; {outstanding.length} subscription
                            {outstanding.length === 1 ? '' : 's'} left active at Stripe
                        </div>
                        <p className="mb-2">
                            The archive itself succeeded and the workspaces are frozen. Nothing was
                            cancelled for the following, so the customer may still be billed.
                            Retrying will not change this &mdash; cancel it in Stripe.
                        </p>
                        <ul className="list-disc space-y-1 pl-5">
                            {outstanding.map((e, i) => (
                                <li key={`${e.effect}-${i}`}>
                                    <span className="font-mono text-xs">{e.effect}</span>
                                    {e.error ? ` — ${e.error}` : ''}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <label htmlFor="archive-reason" className="mb-1 block text-sm font-medium text-foreground">
                    Reason (optional)
                </label>
                <input
                    id="archive-reason"
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. dormant since June"
                    className="mb-4 block w-full rounded-md border-input text-sm focus:border-ring focus:ring-ring"
                />

                <div className="flex justify-end gap-2">
                    {/* After a partial failure the account is already archived, so
                        "Cancel" would be a lie — the only choices left are to retry
                        the outstanding vendor call or to acknowledge it deliberately.
                        Re-submitting is safe: the service re-stamps nothing and writes
                        no second audit row, it just retries what is still uncancelled. */}
                    <button type="button" onClick={partiallyFailed ? onArchived : onClose} disabled={submitting}
                        className="rounded-md border border-input px-3 py-2 text-sm font-medium text-foreground hover:bg-muted/50">
                        {partiallyFailed ? 'Dismiss' : 'Cancel'}
                    </button>
                    {/* The submit button disappears once the only thing left is a
                        subscription we deliberately did not cancel: there is nothing
                        here left to do, and a button that re-runs an archive which
                        has already completed is worse than no button at all. */}
                    {(!partiallyFailed || canRetry) && (
                        <button type="button" onClick={handleArchive} disabled={submitting}
                            className="inline-flex items-center gap-2 rounded-md bg-foreground px-3 py-2 text-sm font-medium text-background hover:opacity-90 disabled:opacity-40">
                            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                            {canRetry ? 'Retry Stripe cancel' : 'Archive account'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ArchiveAccountModal;
